/**
 * PlanCardsGrid — shared plan selection grid used in both OnboardingWizard and PlanSelectionPage
 * Oferta vigente: starter, professional, enterprise (los padres filtran con
 * getOfferedPlans; free/business legacy solo se ven como plan actual).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gift } from 'lucide-react';
import { formatPriceWithCurrency } from '../utils/formatCurrency';
import { hasCustomPricing, getTrialDays, formatFeatureText } from '../utils/plans';
import './PlanCardsGrid.css';

const PLAN_ICONS = {
  free: '🆓',
  starter: '🚀',
  basic: '📦',
  professional: '⭐',
  enterprise: '🏢',
};

function PlanCardsGrid({
  plans,
  selectedPlanId,
  onSelect,
  currentPlanId,
  billingCycle = 'monthly',
  currency = 'ARS',
  showTrial = true,
}) {
  const { t } = useTranslation();

  const getPricing = (plan) => {
    const pricing = plan.pricing?.[currency] || plan.pricing?.ARS || {};
    return pricing;
  };

  return (
    <div className="plan-cards-grid">
      {plans.map((plan) => {
        const isSelected = plan.planId === selectedPlanId;
        const isCurrent = currentPlanId && plan.planId === currentPlanId;
        const isRecommended = plan.planId === 'professional';
        const pricing = getPricing(plan);
        const isEnterprise = hasCustomPricing(plan, currency);
        const trialDays = showTrial && !isCurrent ? getTrialDays(plan) : 0;

        return (
          <div
            key={plan.planId}
            className={`plan-card-item ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''} ${isRecommended ? 'recommended' : ''}`}
            onClick={() => onSelect(plan.planId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSelect(plan.planId)}
          >
            {isRecommended && (
              <div className="plan-card-ribbon">{t('planSelection.recommended')}</div>
            )}
            {isCurrent && (
              <div className="plan-card-current-badge">{t('planSelection.currentPlan')}</div>
            )}

            <div className="plan-card-icon">{PLAN_ICONS[plan.planId] || '📋'}</div>

            <h3 className="plan-card-name">
              {t(`planSelection.plans.${plan.planId}.name`, plan.displayName || plan.name)}
            </h3>
            {(t(`planSelection.plans.${plan.planId}.badge`, '') || plan.badge) && (
              <span className="plan-card-badge">
                {t(`planSelection.plans.${plan.planId}.badge`, plan.badge || '')}
              </span>
            )}

            <div className="plan-card-pricing">
              {isEnterprise ? (
                <span className="plan-price-custom">{t('planSelection.contactUs')}</span>
              ) : pricing.monthly !== null && pricing.monthly !== undefined ? (
                billingCycle === 'annual' ? (
                  <>
                    <span className="plan-price-amount">
                      {formatPriceWithCurrency(
                        pricing.annual != null ? pricing.annual : Math.round(pricing.monthly * 10),
                        currency
                      )}
                    </span>
                    <span className="plan-price-period">/{t('planSelection.year')}</span>
                    {pricing.monthly > 0 && (
                      <div className="plan-price-monthly-equivalent">
                        {formatPriceWithCurrency(
                          pricing.annual != null
                            ? Math.round(pricing.annual / 12)
                            : Math.round((pricing.monthly * 10) / 12),
                          currency
                        )}
                        {t('planSelection.perMonth')}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <span className="plan-price-amount">
                      {formatPriceWithCurrency(pricing.monthly, currency)}
                    </span>
                    <span className="plan-price-period">/{t('planSelection.month')}</span>
                  </>
                )
              ) : (
                <span className="plan-price-free">{t('planSelection.free')}</span>
              )}
            </div>

            {trialDays > 0 && (
              <div className="plan-card-trial-badge">
                <Gift size={16} aria-hidden="true" />
                <span>{t('planSelection.firstDaysFree', { days: trialDays })}</span>
              </div>
            )}

            <p className="plan-card-description">
              {t(`planSelection.plans.${plan.planId}.description`, plan.description)}
            </p>

            <ul className="plan-card-features">
              {(plan.features || []).slice(0, 6).map((feature, idx) => (
                <li key={idx}>
                  <span className="plan-feature-check">✓</span>
                  {formatFeatureText(feature)}
                </li>
              ))}
              {plan.features && plan.features.length > 6 && (
                <li className="plan-feature-more">
                  + {plan.features.length - 6} {t('planSelection.moreFeatures')}
                </li>
              )}
            </ul>

            <div className="plan-card-limits">
              <span>
                🍽️{' '}
                {plan.limits?.maxMenuItems === -1
                  ? t('planSelection.unlimited')
                  : plan.limits?.maxMenuItems || 0}{' '}
                {t('planSelection.menuItemsLabel')}
              </span>
              <span>
                🪑{' '}
                {plan.limits?.maxTables === -1
                  ? t('planSelection.unlimited')
                  : plan.limits?.maxTables || 0}{' '}
                {t('planSelection.tablesLabel')}
              </span>
            </div>

            {isSelected && (
              <div className="plan-card-selected-indicator">
                <span className="plan-selected-check">✓</span> {t('planSelection.selected')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PlanCardsGrid;
